import React, { useState, useEffect, useRef, type FormEvent } from 'react';

import { HN_SKJEMA_ERRORCODES_RESOURCES, HN_SKJEMA_FRONTEND_RESOURCES } from 'src/constants/constants';
import useEventListner from 'src/hooks/useEventListener';
import { useHandleDocumentTitle } from 'src/hooks/useHandleDocumentTitle';
import { useLastActivity } from 'src/hooks/useLastActivity';
import usePageActivation from 'src/hooks/usePageActivation';
import { useResources } from 'src/resources';
import { useHasFetchedRef } from 'src/root/FetchSkjemaContext';

import type { HNSkjemaErrorCodes, HNSkjemaFrontend } from 'src/types/Resources';

import { trackProsesshjelp } from '@helsenorge/framework-utils/adobe-analytics';
import {
  SessionTimeoutAction,
  type ShowSignOutBoxData,
  HNeventSetOnShowSignoutbox,
} from '@helsenorge/framework-utils/web-component/events';
import { getFormDefinition } from '@helsenorge/refero';

import ConfirmationLightbox from './lightboxes/confirmationlightbox';
import ConfirmationLightboxLagre from './lightboxes/confirmationlightbox-lagre';
import { useAppDispatch, useAppSelector } from '../store/reducers';
import { isKopiLagret, skalViseStandardmeldingOmPdfErFalseOgIkkeRecipient } from '../util';
import RenderSkjema from './RenderSkjema';
import { dispatchSetSessionTimeoutFn } from '../util/webcomp-utils';
import { AnonomousSkjemaLightbox } from './modals/AnonomousSkjemaLightbox';
import AnonymousUploadDeniedModal from './modals/AnonymousUploadDeniedModal';
import AttachementErrorModal from './modals/AttachementErrorModal';
import KanIkkeLagreModal from './modals/KanIkkeLagreModal';
import LagreSkjemaErrorModal from './modals/LagreSkjemaErrorModal';
import SendInnSkjemaErrorModal from './modals/SendInnSkjemaErrorModal';
import LoadSkjemaWarningMessageNotification from './notifications/LoadSkjemaWarningMessageNotification';
import SkjemaLoadError from './notifications/skjemaLoadError';
import RenderSidebar from './sidebar/RenderSidebar';
import { shouldRender, SkjemaSpinners } from './SkjemaSpinners';
import { fetchSkjemaoppgave } from '../store/actions/getSkjemaActionCreator';

const Skjemautfyller = (): React.JSX.Element | null => {
  useResources<HNSkjemaErrorCodes>(HN_SKJEMA_ERRORCODES_RESOURCES);
  useResources<HNSkjemaFrontend>(HN_SKJEMA_FRONTEND_RESOURCES);
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [timeSkjemaLoadedForUser] = useState(() => Date.now());
  const { cancelTimers, refreshTokenIfExpiringSoon } = useEventListner({ timeSkjemaLoadedForUser });
  const dispatch = useAppDispatch();
  const { setLastActivity, lastActivity } = useLastActivity();
  const lastActivityRef = useRef(lastActivity);
  const uiState = useAppSelector(state => state.refero.ui);
  const formDefinition = useAppSelector(state => getFormDefinition(state));

  const isMicroweb = useAppSelector(state => state.microweb.isMicroweb);
  const isMicrowebStep = useAppSelector(state => state.microweb.isMicrowebStep);
  const isMicrowebOppgave = useAppSelector(state => state.microweb.isMicrowebOppgave);

  const showLocalSpinner = useAppSelector(state => state.refero.ui.showLocalSpinner);
  const showGlobalSpinner = useAppSelector(state => state.refero.ui.showGlobalSpinner);

  useEffect(() => {
    lastActivityRef.current = lastActivity;
  }, [lastActivity]);

  useHandleDocumentTitle(isMicrowebStep || isMicroweb);
  usePageActivation((activateInfo): void => {
    if (activateInfo.reason === 'visible') {
      HNeventSetOnShowSignoutbox((data: ShowSignOutBoxData): SessionTimeoutAction => {
        const currentIdleTime = Date.now() - lastActivityRef.current;
        const idleRatio = currentIdleTime / (data.tokenLifetime - data.tokenWarnUserWhenRemaining);
        const action =
          idleRatio > 0.5
            ? SessionTimeoutAction.Default
            : idleRatio > 0
              ? SessionTimeoutAction.RequestExtension
              : SessionTimeoutAction.RequestLogout;
        return action;
      });
    }
  });
  const updateDocumentTitle = (): void => {
    if (!isMicroweb && !isMicrowebStep && !isMicrowebOppgave) {
      if (formDefinition && formDefinition.Content && formDefinition.Content.title) {
        document.title = `${formDefinition.Content.title} - Helsenorge`;
      } else {
        document.title = 'Skjemautfyller - Helsenorge';
      }
    }
  };

  const onShowSignOutBox = (data: ShowSignOutBoxData): SessionTimeoutAction => {
    const currentIdleTime = Date.now() - lastActivityRef.current;
    const idleRatio = currentIdleTime / (data.tokenLifetime - data.tokenWarnUserWhenRemaining);
    const action = idleRatio > 0.5 ? SessionTimeoutAction.Default : SessionTimeoutAction.RequestExtension;
    return action;
  };

  const toggleSidebarOpen = (e: Event | FormEvent<unknown> | undefined): void => {
    e?.stopPropagation();
    e?.preventDefault();
    setIsSidebarOpen(prevstate => {
      trackProsesshjelp('Prosesshjelp', 'Hjelp', 'Om skjema', prevstate ? 'Open' : 'Close');
      return !prevstate;
    });
  };
  const hasFetchedRef = useHasFetchedRef();

  useEffect(() => {
    if (!hasFetchedRef.current) {
      dispatch(fetchSkjemaoppgave({ language: undefined }));
      hasFetchedRef.current = true;
    }
    document.getElementsByTagName('html')[0].classList.add('page_refero__fixed-scroll-offset');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (formDefinition?.Content) {
      dispatchSetSessionTimeoutFn(onShowSignOutBox);
    }
  }, [formDefinition?.Content]);

  useEffect(() => {
    updateDocumentTitle();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formDefinition]);

  if (uiState.loadSkjemaoppgaveErrorMessage || uiState.loadSkjemaoppgaveErrorCode) {
    return <SkjemaLoadError />;
  }
  return (
    <div>
      <div className="page_refero__content">
        <SkjemaSpinners />
        <LoadSkjemaWarningMessageNotification />
        {shouldRender(isMicroweb, isMicrowebStep, showLocalSpinner, showGlobalSpinner) && (
          <RenderSkjema
            setLastActivity={setLastActivity}
            timeSkjemaLoadedForUser={timeSkjemaLoadedForUser}
            openSidebar={toggleSidebarOpen}
            lastActivity={lastActivity}
            refreshTokenIfExpiringSoon={refreshTokenIfExpiringSoon}
          />
        )}
      </div>
      <RenderSidebar isSidebarOpen={isSidebarOpen && !isMicroweb} toggleSidebarOpen={toggleSidebarOpen} />
      <AnonomousSkjemaLightbox />
      <SendInnSkjemaErrorModal />
      <LagreSkjemaErrorModal />
      <KanIkkeLagreModal />
      <ConfirmationLightbox
        pdfFalseOgIkkeRecipient={skalViseStandardmeldingOmPdfErFalseOgIkkeRecipient(formDefinition?.Content)} // Viser standardmelding hvis pdf er false og ikke recipient
        erKopiLagret={formDefinition?.Content ? isKopiLagret(formDefinition?.Content) : true}
      />
      <ConfirmationLightboxLagre cancelTimers={cancelTimers} />
      <AnonymousUploadDeniedModal />
      <AttachementErrorModal />
    </div>
  );
};

export default Skjemautfyller;
